import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DragDropModule, CdkDragEnd, CdkDragDrop, CdkDragStart } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppState, CompanyService, DocumentService } from '../common';
import { Document, DocumentUsage } from '../model';
import { PreviewDocument } from '../common/service/document.service';
import { ManagedDocument } from '../common/models/document.model';
import { CanvasStateService } from './canvas-state.service';
import { CanvasDocumentView, CanvasGroup } from './canvas.models';
import { DocumentGroupComponent } from './document-group.component';
import { DocumentCardComponent } from './document-card.component';
import { DocumentListTableComponent, DocumentUploadedSortField } from './document-list-table.component';
import { hashBlob } from './document-hash.util';
import { CompanyWriteAccess } from '../common/service/company-write-access.service';

@Component({
  selector: 'app-document-canvas',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DragDropModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule,
    DocumentListTableComponent,
    DocumentGroupComponent,
    DocumentCardComponent,
  ],
  templateUrl: './document-canvas.component.html',
  styleUrls: ['./document-canvas.component.scss'],
})
export class DocumentCanvasComponent implements OnInit, OnChanges {
  /** Mirrors the server's `Company.mayWrite` — see `CompanyWriteAccess`. */
  readonly readOnly = inject(CompanyWriteAccess).readOnly;

  private readonly canvasState = inject(CanvasStateService);
  private readonly companyService = inject(CompanyService);
  private readonly documentService = inject(DocumentService);
  private readonly appState = inject(AppState);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(MatDialogRef, { optional: true });
  private readonly dialog = inject(MatDialog);
  constructor(
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData?: {
      documents?: Document[];
      storageKey?: string | null;
      previewMap?: Record<string, PreviewDocument>;
      selectionMode?: boolean;
      selectedDocumentIds?: string[];
      documentTypeId?: string | null;
      category?: string | null;
    }
  ) {}
  private readonly filterDocumentTypeId = this.dialogData?.documentTypeId ?? null;
  private readonly filterCategory = this.dialogData?.category ?? null;
  @ViewChild('surface', { static: true }) private readonly surfaceRef?: ElementRef<HTMLElement>;

  @Input() documents: Document[] = [];
  @Input() storageKey: string | null = null;
  @Input() previewMap: Record<string, PreviewDocument> = {};
  @Output() changed = new EventEmitter<void>();

  readonly sourceDocuments = signal<Document[]>([]);
  readonly allDocuments = signal<Document[]>([]);
  readonly localPreviewMap = signal<Record<string, PreviewDocument>>({});
  readonly sourceLoading = signal(false);
  readonly canvasDocumentsLoading = signal(true);
  readonly sourcePageIndex = signal(0);
  readonly sourcePageSize = signal(20);
  readonly sourceTotalElements = signal(0);
  readonly sourceSortField = signal<DocumentUploadedSortField>('date');
  readonly sourceSortDirection = signal<'asc' | 'desc'>('desc');
  readonly groups = this.canvasState.groupsOnCanvas;
  readonly canvasDocuments = this.canvasState.canvasDocuments;
  readonly looseDocuments = computed(() => this.canvasDocuments().filter((document) => !document.groupId));
  readonly placedDocumentIds = computed(() => new Set(this.canvasDocuments().map((document) => document.documentId)));
  readonly remainingDocumentCount = computed(() => {
    const total = this.sourceTotalElements() || this.allDocuments().length || this.getDocuments().length;
    return Math.max(0, total - this.placedDocumentIds().size);
  });
  readonly groupDropListIds = computed(() => this.groups().map((group) => this.dropListId(group.id)));
  readonly allConnectedDropListIds = computed(() => [
    'canvas-source-list',
    'canvas-stage-list',
    ...this.groupDropListIds(),
  ]);
  private readonly dragOrigins = new Map<string, { x: number; y: number }>();
  /** Pointer offset inside a card at pickup, so release keeps that same point under the cursor. */
  private readonly documentPickupOffsets = new Map<string, { x: number; y: number }>();
  /** A document changed groups/was merged, so its loose-card end handler must not move it. */
  private readonly handledDropPlacementIds = new Set<string>();
  private groupPointerOrigin: { groupId: string; pointerX: number; pointerY: number; x: number; y: number } | null =
    null;
  private cachedPreviewMap: Record<string, PreviewDocument> = {};
  private cachedPreviewInputs: {
    dialog: Record<string, PreviewDocument>;
    input: Record<string, PreviewDocument>;
    local: Record<string, PreviewDocument>;
  } | null = null;
  protected readonly autosaveState = signal<'saved' | 'saving'>('saved');
  readonly documentDragActive = signal(false);
  readonly activeDocumentPlacementId = signal<string | null>(null);
  readonly groupDragActive = signal(false);
  readonly activeGroupId = signal<string | null>(null);
  readonly documentDropPredicate = (drag: { data: unknown }) =>
    this.isDocument(drag.data as Document | CanvasDocumentView | CanvasGroup);
  private autosaveTimer?: ReturnType<typeof setTimeout>;
  readonly documentRowTooltip = (document: Document | ManagedDocument) => {
    const placements = this.canvasDocuments().filter((entry) => entry.documentId === document.id);
    if (!placements.length) {
      return this.translate.instant('canvas_drag_to_canvas_tr');
    }
    if (!placements.some((entry) => entry.groupId)) {
      return this.translate.instant('canvas_placed_on_canvas_tr');
    }
    const groupCount = new Set(placements.filter((entry) => entry.groupId).map((entry) => entry.groupId)).size;
    return groupCount === 1
      ? this.translate.instant('canvas_linked_to_group_tr')
      : this.translate.instant('canvas_linked_to_groups_tr', { count: groupCount });
  };
  readonly highlightedGroupId = signal<string | null>(null);
  readonly highlightedDocumentPlacementId = signal<string | null>(null);
  private highlightedGroupTimer?: ReturnType<typeof setTimeout>;
  readonly sidebarCollapsed = signal(false);
  readonly canvasZoom = signal(1);
  readonly selectionMode = this.dialogData?.selectionMode ?? false;
  readonly selectedDocumentIds = signal<Set<string>>(new Set(this.dialogData?.selectedDocumentIds ?? []));

  toggleDocumentSelection(documentId: string, selected: boolean) {
    this.selectedDocumentIds.update((current) => {
      const next = new Set(current);
      selected ? next.add(documentId) : next.delete(documentId);
      return next;
    });
  }
  eventChecked(event: Event) {
    return (event.target as HTMLInputElement).checked;
  }

  toggleGroupSelection(groupId: string, selected: boolean) {
    const ids = this.documentsForGroup(groupId).map((document) => document.id);
    this.selectedDocumentIds.update((current) => {
      const next = new Set(current);
      ids.forEach((id) => (selected ? next.add(id) : next.delete(id)));
      return next;
    });
  }

  isGroupSelected(groupId: string) {
    const ids = this.documentsForGroup(groupId).map((document) => document.id);
    return ids.length > 0 && ids.every((id) => this.selectedDocumentIds().has(id));
  }

  isGroupPartiallySelected(groupId: string) {
    const ids = this.documentsForGroup(groupId).map((document) => document.id);
    const selected = ids.filter((id) => this.selectedDocumentIds().has(id)).length;
    return selected > 0 && selected < ids.length;
  }
  readonly localFileDropActive = signal(false);

  @HostListener('dragover', ['$event'])
  handleLocalFileDragOver(event: DragEvent): void {
    if (!this.hasLocalFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.localFileDropActive.set(true);
  }

  @HostListener('dragleave', ['$event'])
  handleLocalFileDragLeave(event: DragEvent): void {
    if (!this.hasLocalFiles(event)) return;
    if (!event.relatedTarget || !(event.currentTarget as HTMLElement)?.contains(event.relatedTarget as Node)) {
      this.localFileDropActive.set(false);
    }
  }

  @HostListener('drop', ['$event'])
  async handleLocalFileDrop(event: DragEvent): Promise<void> {
    if (!this.hasLocalFiles(event)) return;
    event.preventDefault();
    this.localFileDropActive.set(false);
    const file = Array.from(event.dataTransfer?.files ?? [])[0];
    if (!file) return;
    const position = this.toCanvasPosition(event.clientX, event.clientY);
    await this.uploadLocalFileToCanvas(file, position.x, position.y);
  }

  private hasLocalFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  private async uploadLocalFileToCanvas(file: File, x: number, y: number): Promise<void> {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) return;
    const title = file.name.replace(/\.[^/.]+$/, '') || 'Document';
    const mimeType = file.type || (extension === 'pdf' ? 'application/pdf' : 'image/' + extension);
    const blob = file.slice(0, file.size, mimeType);
    const tempDocument = {
      id: crypto.randomUUID(),
      title,
      mimeType,
      extension,
      kind: extension === 'pdf' ? 'pdf' : 'image',
      blob,
      size: file.size,
      createdAt: new Date().toISOString(),
      pages: 1,
      source: 'upload',
    } as ManagedDocument;
    const fileHash = await hashBlob(blob);
    let suggestion: any;
    try {
      suggestion = (await this.documentService.analyzeDocument(file, fileHash))?.analysis;
    } catch (error) {
      console.warn('Failed to analyze dropped document', error);
    }
    const { DocumentWorkbenchDialogComponent } = await import('./document-workbench-dialog.component');
    const result = await firstValueFrom(
      this.dialog
        .open(DocumentWorkbenchDialogComponent, {
          data: {
            mode: 'preview',
            document: tempDocument,
            allowSaveUpload: true,
            actionLabels: 'local',
            suggestedDocumentTypeId: suggestion?.documentTypeId ?? null,
            suggestedDocumentTypeName: suggestion?.documentType ?? null,
            suggestedDocumentTypeConfidence: suggestion?.documentTypeConfidence ?? null,
          },
          height: '100dvh',
          maxHeight: '100dvh',
          width: '100vw',
          maxWidth: '100vw',
          panelClass: 'document-workbench-dialog',
        })
        .afterClosed()
    );
    if (!result || result.kind !== 'metadata' || result.action !== 'save-upload' || !result.metadata.documentTypeId)
      return;
    const uploaded = await this.documentService.uploadWithDuplicateConfirm(
      file,
      result.metadata.documentTypeId,
      result.metadata.title,
      () => {},
      { fileHash }
    );
    if (!uploaded) return;
    this.canvasState.addDocument(uploaded.id, Math.max(24, x - 80), Math.max(24, y - 56));
    await this.loadAllDocuments();
    await this.loadSourceDocuments(0, this.sourcePageSize());
    await this.mergePreviewMap([uploaded]);
    this.emitChanged();
  }

  ngOnInit(): void {
    this.initializeCanvas();
    void this.loadAllDocuments();
    void this.loadSourceDocuments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initializeCanvas();
    void this.loadAllDocuments();
    void this.loadSourceDocuments();
  }

  private initializeCanvas() {
    this.canvasState.setStorageKey(this.getStorageKey());
  }

  private getDocuments() {
    return this.documents?.length ? this.documents : (this.dialogData?.documents ?? []);
  }

  private getAllDocuments() {
    return this.allDocuments().length ? this.allDocuments() : this.getDocuments();
  }

  private getStorageKey() {
    return this.storageKey ?? this.dialogData?.storageKey ?? null;
  }

  getPreviewMap() {
    const dialog = (this.dialogData?.previewMap ?? {}) as Record<string, PreviewDocument>;
    const input = this.previewMap ?? {};
    const local = this.localPreviewMap() ?? {};
    if (
      this.cachedPreviewInputs?.dialog === dialog &&
      this.cachedPreviewInputs.input === input &&
      this.cachedPreviewInputs.local === local
    ) {
      return this.cachedPreviewMap;
    }
    this.cachedPreviewInputs = { dialog, input, local };
    this.cachedPreviewMap = { ...dialog, ...input, ...local };
    return this.cachedPreviewMap;
  }

  private async loadSourceDocuments(page = this.sourcePageIndex(), size = this.sourcePageSize()) {
    const companyId = this.appState.companySite$.value?.company?.id;
    if (!companyId) {
      const docs = this.getDocuments();
      this.sourceDocuments.set(docs);
      this.sourceTotalElements.set(docs.length);
      await this.mergePreviewMap(docs);
      return;
    }
    this.sourceLoading.set(true);
    try {
      const pageData = await this.companyService.allDocumentsForCompanyTeam(companyId, {
        page,
        size,
        sort: `${this.sourceSortField()},${this.sourceSortDirection()}`,
        ...(this.filterCategory ? { documentTypeCategory: this.filterCategory } : {}),
        ...(!this.filterCategory && this.filterDocumentTypeId ? { documentType: this.filterDocumentTypeId } : {}),
      });
      this.sourceDocuments.set(pageData?.content ?? []);
      this.sourceTotalElements.set(pageData?.totalElements ?? 0);
      this.sourcePageIndex.set(pageData?.pageNumber ?? page);
      this.sourcePageSize.set(pageData?.pageSize ?? size);
      await this.mergePreviewMap(pageData?.content ?? []);
    } catch (error) {
      console.error('Failed to load canvas documents', error);
      this.sourceDocuments.set([]);
      this.sourceTotalElements.set(0);
    } finally {
      this.sourceLoading.set(false);
    }
  }

  private async loadAllDocuments() {
    this.canvasDocumentsLoading.set(true);
    const companyId = this.appState.companySite$.value?.company?.id;
    if (!companyId) {
      const docs = this.getDocuments();
      this.allDocuments.set(docs);
      this.canvasState.setSourceDocuments(docs, Boolean(this.filterCategory || this.filterDocumentTypeId));
      await this.mergePreviewMap(docs);
      this.canvasDocumentsLoading.set(false);
      return;
    }
    try {
      const firstPage = await this.companyService.allDocumentsForCompanyTeam(companyId, {
        page: 0,
        size: 1,
        sort: `${this.sourceSortField()},${this.sourceSortDirection()}`,
        ...(this.filterCategory ? { documentTypeCategory: this.filterCategory } : {}),
        ...(!this.filterCategory && this.filterDocumentTypeId ? { documentType: this.filterDocumentTypeId } : {}),
      });
      const totalElements = firstPage?.totalElements ?? firstPage?.content?.length ?? 0;
      if (!totalElements) {
        this.allDocuments.set([]);
        this.canvasState.setSourceDocuments([], Boolean(this.filterCategory || this.filterDocumentTypeId));
        this.canvasDocumentsLoading.set(false);
        return;
      }
      const allPage = await this.companyService.allDocumentsForCompanyTeam(companyId, {
        page: 0,
        size: totalElements,
        sort: `${this.sourceSortField()},${this.sourceSortDirection()}`,
        ...(this.filterCategory ? { documentTypeCategory: this.filterCategory } : {}),
        ...(!this.filterCategory && this.filterDocumentTypeId ? { documentType: this.filterDocumentTypeId } : {}),
      });
      const docs = allPage?.content ?? [];
      this.allDocuments.set(docs);
      this.canvasState.setSourceDocuments(docs, Boolean(this.filterCategory || this.filterDocumentTypeId));
      await this.mergePreviewMap(docs);
    } catch (error) {
      console.error('Failed to load full canvas document cache', error);
      const fallback = this.getDocuments();
      this.allDocuments.set(fallback);
      this.canvasState.setSourceDocuments(fallback, Boolean(this.filterCategory || this.filterDocumentTypeId));
      await this.mergePreviewMap(fallback);
    } finally {
      this.canvasDocumentsLoading.set(false);
    }
  }

  createGroup() {
    this.canvasState.createGroup();
    this.emitChanged();
  }

  renameGroup(groupId: string, title: string) {
    this.canvasState.renameGroup(groupId, title);
    this.emitChanged();
  }

  deleteGroup(groupId: string) {
    this.canvasState.deleteGroup(groupId);
    this.emitChanged();
  }

  removePlacement(placementId: string) {
    this.canvasState.removePlacement(placementId);
    this.emitChanged();
  }

  handleSourcePageChange(event: { pageIndex: number; pageSize: number }) {
    this.sourcePageIndex.set(event.pageIndex);
    this.sourcePageSize.set(event.pageSize);
    void this.loadSourceDocuments(event.pageIndex, event.pageSize);
  }

  handleSourceSortChange(field: DocumentUploadedSortField) {
    if (this.sourceSortField() === field) {
      this.sourceSortDirection.set(this.sourceSortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sourceSortField.set(field);
      this.sourceSortDirection.set(field === 'title' ? 'asc' : 'desc');
    }
    this.sourcePageIndex.set(0);
    void this.loadSourceDocuments(0, this.sourcePageSize());
  }

  handleNavigateUsage(usage: DocumentUsage) {
    const cs = this.appState.companySite$.value;
    if (!cs?.company) {
      return;
    }
    const companyId = cs.company.id;
    const siteId = cs.site?.id ?? cs.company.sites?.[0]?.id;
    if (!siteId) {
      return;
    }
    this.close();
    void this.router.navigateByUrl(`/company/${companyId}/site/${siteId}/${usage.link}`);
  }

  close() {
    this.dialogRef?.close(
      this.selectionMode ? { selectedDocumentIds: Array.from(this.selectedDocumentIds()) } : undefined
    );
  }

  linkSelectedDocuments() {
    if (this.selectedDocumentIds().size > 0) {
      this.close();
    }
  }

  toggleSidebar() {
    this.sidebarCollapsed.update((current) => !current);
  }

  zoomCanvas(delta: number) {
    this.canvasZoom.update((value) => Math.min(1.5, Math.max(0.25, Number((value + delta).toFixed(2)))));
  }

  resetCanvasZoom() {
    this.canvasZoom.set(1);
  }

  handleCanvasDrop(event: CdkDragDrop<any[]>) {
    const dragged = event.item.data as Document | CanvasDocumentView | CanvasGroup;
    if (!dragged) {
      return;
    }
    if (this.isDocument(dragged)) {
      const { x, y } = this.toCanvasPosition(event.dropPoint.x, event.dropPoint.y);
      const targetGroupId = this.groupAtPoint(x, y);
      const targetDocument = this.looseDocumentAtPoint(x, y, 'placementId' in dragged ? dragged.placementId : null);
      if (targetDocument) {
        if ('placementId' in dragged) {
          this.handledDropPlacementIds.add(dragged.placementId);
        }
        this.canvasState.createGroupForDocuments(
          [dragged.id, targetDocument.id],
          Math.max(24, targetDocument.x - 24),
          Math.max(24, targetDocument.y - 24)
        );
        this.documentDragActive.set(false);
        this.highlightedDocumentPlacementId.set(null);
        this.emitChanged();
        return;
      }
      if ('placementId' in dragged && dragged.groupId === targetGroupId) {
        // A loose card's drag-end handler persists its visible rectangle. This
        // avoids CDK's dropPoint becoming incorrect after a rapid direction change.
        this.documentDragActive.set(false);
        this.highlightedDocumentPlacementId.set(null);
        return;
      }
      if ('placementId' in dragged) {
        this.canvasState.removePlacement(dragged.placementId);
      }
      const placement = targetGroupId ? { x, y } : this.centerDropPosition(x, y);
      this.canvasState.addDocument(dragged.id, placement.x, placement.y, targetGroupId);
      this.documentDragActive.set(false);
      if (targetGroupId) {
        this.flashGroup(targetGroupId);
      }
      this.emitChanged();
    }
  }

  handleGroupDrop(groupId: string, event: CdkDragDrop<any[]>) {
    const dragged = event.item.data as Document | CanvasDocumentView | CanvasGroup;
    if (this.isDocument(dragged)) {
      const { x, y } = this.toCanvasPosition(event.dropPoint.x, event.dropPoint.y);
      if ('placementId' in dragged && dragged.groupId === groupId) {
        this.handledDropPlacementIds.add(dragged.placementId);
        event.item.reset();
        this.flashGroup(groupId);
        return;
      }
      if ('placementId' in dragged) {
        this.handledDropPlacementIds.add(dragged.placementId);
        this.canvasState.removePlacement(dragged.placementId);
      }
      this.canvasState.addDocument(dragged.id, x, y, groupId);
      this.documentDragActive.set(false);
      this.flashGroup(groupId);
      this.emitChanged();
    }
  }

  handleLooseDocumentDragEnd(document: CanvasDocumentView, event: CdkDragEnd) {
    this.highlightedDocumentPlacementId.set(null);
    this.documentDragActive.set(false);
    this.activeDocumentPlacementId.set(null);
    const pickupOffset = this.documentPickupOffsets.get(document.placementId) ?? { x: 80, y: 56 };
    // `dropPoint` is the pointer release coordinate. Subtracting the pickup
    // offset preserves the exact point inside the card which the user grabbed.
    const position = this.toCanvasPosition(event.dropPoint.x - pickupOffset.x, event.dropPoint.y - pickupOffset.y);
    this.dragOrigins.delete(document.placementId);
    if (!this.handledDropPlacementIds.delete(document.placementId)) {
      // Update the persisted left/top before clearing CDK's temporary transform.
      // This keeps the card at its visual release position without a snap/flicker.
      this.canvasState.movePlacement(document.placementId, position.x, position.y);
      this.emitChanged();
    }
    event.source.reset();
  }

  handleLooseDocumentPointerDown(document: CanvasDocumentView, event: PointerEvent) {
    const card = event.currentTarget as HTMLElement;
    const rect = card.getBoundingClientRect();
    this.documentPickupOffsets.set(document.placementId, {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  }

  handleLooseDocumentDragStart(document: CanvasDocumentView, _event: CdkDragStart) {
    this.documentDragActive.set(true);
    this.activeDocumentPlacementId.set(document.placementId);
    this.dragOrigins.set(document.placementId, { x: document.x, y: document.y });
  }

  handleSourceRowDragMove(event: {
    document: Document | ManagedDocument;
    event: { pointerPosition: { x: number; y: number } };
  }) {
    const { x, y } = this.toCanvasPosition(event.event.pointerPosition.x, event.event.pointerPosition.y);
    const groupId = this.groupAtPoint(x, y);
    const targetDocument = this.looseDocumentAtPoint(x, y, this.activeDocumentPlacementId());
    this.highlightedDocumentPlacementId.set(targetDocument?.placementId ?? null);
    if (groupId) {
      this.highlightGroup(groupId);
      return;
    }
    this.highlightedGroupId.set(null);
  }

  handleCanvasDragMove(event: { pointerPosition: { x: number; y: number }; source?: { data?: unknown } }) {
    if (this.groupDragActive()) {
      this.highlightedGroupId.set(null);
      return;
    }
    const { x, y } = this.toCanvasPosition(event.pointerPosition.x, event.pointerPosition.y);
    const groupId = this.groupAtPoint(x, y);
    const dragged = event.source?.data as Partial<CanvasDocumentView> | undefined;
    const targetDocument = this.looseDocumentAtPoint(x, y, dragged?.placementId ?? this.activeDocumentPlacementId());
    this.highlightedDocumentPlacementId.set(targetDocument?.placementId ?? null);
    if (groupId) {
      this.highlightGroup(groupId);
    } else {
      this.highlightedGroupId.set(null);
    }
  }

  handleGroupedDragEnd(event: CdkDragEnd) {
    const dragged = event.source.data as CanvasDocumentView | Document | CanvasGroup;
    if (this.isDocument(dragged) && 'placementId' in dragged) {
    }
    this.documentDragActive.set(false);
    this.activeDocumentPlacementId.set(null);
    this.highlightedDocumentPlacementId.set(null);
    if (!this.isDocument(dragged)) {
      return;
    }
    const origin =
      'placementId' in dragged
        ? (this.dragOrigins.get(dragged.placementId) ?? { x: dragged.x, y: dragged.y })
        : { x: 0, y: 0 };
    const pos = {
      x: origin.x + this.toCanvasDistance(event.distance.x),
      y: origin.y + this.toCanvasDistance(event.distance.y),
    };
    if ('placementId' in dragged) {
      this.canvasState.movePlacement(dragged.placementId, pos.x, pos.y);
      // See handleLooseDocumentDragEnd: the canvas owns the final position.
      event.source.reset();
      this.dragOrigins.delete(dragged.placementId);
    }
    this.emitChanged();
  }

  handleGroupDragEnd(group: CanvasGroup, event: CdkDragEnd) {
    const dragged = event.source.data as CanvasDocumentView | Document | CanvasGroup | undefined;
    if (!dragged || 'documentId' in dragged) {
      this.groupDragActive.set(false);
      this.activeGroupId.set(null);
      return;
    }
    const origin = this.dragOrigins.get(group.id) ?? { x: group.x, y: group.y };
    const pos = {
      x: origin.x + this.toCanvasDistance(event.distance.x),
      y: origin.y + this.toCanvasDistance(event.distance.y),
    };
    this.canvasState.moveGroup(group.id, pos.x, pos.y);
    this.dragOrigins.delete(group.id);
    this.groupDragActive.set(false);
    this.activeGroupId.set(null);
    this.emitChanged();
  }

  handleGroupDragStart(group: CanvasGroup, _event: CdkDragStart) {
    if (this.groupDragActive()) {
      return;
    }
    this.groupDragActive.set(true);
    this.activeGroupId.set(group.id);
    this.highlightedGroupId.set(null);
    this.dragOrigins.set(group.id, { x: group.x, y: group.y });
  }

  handleGroupPointerStart(group: CanvasGroup, event: PointerEvent) {
    if (this.documentDragActive() || this.groupDragActive()) {
      return;
    }
    this.groupDragActive.set(true);
    this.activeGroupId.set(group.id);
    this.highlightedGroupId.set(null);
    this.groupPointerOrigin = {
      groupId: group.id,
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: group.x,
      y: group.y,
    };
  }

  handleGroupPointerMove(group: CanvasGroup, event: PointerEvent) {
    const origin = this.groupPointerOrigin;
    if (!origin || origin.groupId !== group.id) {
      return;
    }
    this.canvasState.moveGroup(
      group.id,
      origin.x + this.toCanvasDistance(event.clientX - origin.pointerX),
      origin.y + this.toCanvasDistance(event.clientY - origin.pointerY)
    );
  }

  handleGroupPointerEnd(group: CanvasGroup, _event: PointerEvent) {
    if (this.groupPointerOrigin?.groupId !== group.id) {
      return;
    }
    this.groupPointerOrigin = null;
    this.groupDragActive.set(false);
    this.activeGroupId.set(null);
    this.emitChanged();
  }

  dropListId(groupId: string) {
    return `canvas-group-list-${groupId}`;
  }

  connectedDropListIds(excludeGroupId?: string) {
    const excludedId = excludeGroupId ? this.dropListId(excludeGroupId) : null;
    return this.allConnectedDropListIds().filter((id) => id !== excludedId);
  }

  highlightGroup(groupId: string) {
    if (this.highlightedGroupTimer) {
      clearTimeout(this.highlightedGroupTimer);
      this.highlightedGroupTimer = undefined;
    }
    if (this.highlightedGroupId() !== groupId) {
      this.highlightedGroupId.set(groupId);
    }
  }

  clearGroupHighlight(groupId: string) {
    if (this.highlightedGroupTimer) {
      clearTimeout(this.highlightedGroupTimer);
    }
    this.highlightedGroupTimer = setTimeout(() => {
      if (this.highlightedGroupId() === groupId) {
        this.highlightedGroupId.set(null);
      }
    }, 180);
  }

  documentsForGroup(groupId: string) {
    return this.canvasDocuments().filter((document) => document.groupId === groupId);
  }

  trackByDocumentId(_: number, document: CanvasDocumentView) {
    return document.placementId;
  }

  trackByGroupId(_: number, group: CanvasGroup) {
    return group.id;
  }

  groupWidth(group: CanvasGroup) {
    return Math.max(group.width, 380);
  }

  groupHeight(group: CanvasGroup) {
    return Math.max(group.height, 320);
  }

  previewSrcFor(document: CanvasDocumentView) {
    return this.getPreviewMap()[document.id]?.preview
      ? `data:image/png;base64,${this.getPreviewMap()[document.id].preview}`
      : '';
  }

  private async mergePreviewMap(documents: Document[]) {
    const ids = documents.map((document) => document.id).filter(Boolean);
    if (!ids.length) {
      return;
    }
    try {
      const previewMap = await this.documentService.previewList(ids);
      this.localPreviewMap.update((current) => ({ ...current, ...previewMap }));
    } catch (error) {
      console.error('Failed to load canvas previews', error);
    }
  }

  private groupAtPoint(x: number, y: number): string | null {
    const group = [...this.groups()].reverse().find((entry) => {
      const withinStoredBounds =
        x >= entry.x && x <= entry.x + entry.width && y >= entry.y && y <= entry.y + entry.height;
      if (withinStoredBounds) return true;
      const element = document.querySelector<HTMLElement>(`[data-canvas-group-id="${entry.id}"]`);
      const surface = this.surfaceRef?.nativeElement;
      if (!element || !surface) return false;
      const groupRect = element.getBoundingClientRect();
      const surfaceRect = surface.getBoundingClientRect();
      const zoom = this.canvasZoom();
      const screenX = surfaceRect.left + x * zoom;
      const screenY = surfaceRect.top + y * zoom;
      return (
        screenX >= groupRect.left &&
        screenX <= groupRect.right &&
        screenY >= groupRect.top &&
        screenY <= groupRect.bottom
      );
    });
    return group?.id ?? null;
  }

  private looseDocumentAtPoint(x: number, y: number, excludePlacementId: string | null): CanvasDocumentView | null {
    return (
      [...this.looseDocuments()]
        .reverse()
        .find(
          (document) =>
            document.placementId !== excludePlacementId &&
            x >= document.x &&
            x <= document.x + 180 &&
            y >= document.y &&
            y <= document.y + 240
        ) ?? null
    );
  }

  documentMergeIndicatorX(placementId: string) {
    const document = this.looseDocuments().find((entry) => entry.placementId === placementId);
    return (document?.x ?? 0) + 90;
  }

  documentMergeIndicatorY(placementId: string) {
    const document = this.looseDocuments().find((entry) => entry.placementId === placementId);
    return (document?.y ?? 0) - 34;
  }

  private isDocument(value: Document | CanvasDocumentView | CanvasGroup): value is Document {
    const candidate = value as Partial<Document>;
    return (
      !!value &&
      typeof candidate.id === 'string' &&
      typeof candidate.title === 'string' &&
      ('type' in candidate || 'fileType' in candidate || 'mimeType' in candidate)
    );
  }

  private toCanvasPosition(x: number, y: number) {
    const surface = this.surfaceRef?.nativeElement;
    if (!surface) {
      return { x, y };
    }
    const rect = surface.getBoundingClientRect();
    const zoom = this.canvasZoom();
    return {
      x: (x - rect.left) / zoom,
      y: (y - rect.top) / zoom,
    };
  }

  /** Converts pointer/CDK movement (screen pixels) to unzoomed surface pixels. */
  private toCanvasDistance(distance: number) {
    return distance / this.canvasZoom();
  }

  private centerDropPosition(x: number, y: number) {
    return {
      x: Math.max(24, x - 80),
      y: Math.max(24, y - 56),
    };
  }

  private flashGroup(groupId: string) {
    if (this.highlightedGroupTimer) {
      clearTimeout(this.highlightedGroupTimer);
      this.highlightedGroupTimer = undefined;
    }
    this.highlightedGroupId.set(groupId);
    setTimeout(() => {
      if (this.highlightedGroupId() === groupId) {
        this.highlightedGroupId.set(null);
      }
    }, 900);
  }

  private emitChanged() {
    this.autosaveState.set('saving');
    this.changed.emit();
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
    }
    this.autosaveTimer = setTimeout(() => this.autosaveState.set('saved'), 350);
  }
}
